// L'équipe gratuite (D-153, D-159, D-166) : logique pure (testée), sans réseau ni écran.
//  - l'état d'un club en mode gratuit tel que le rend la base (free_my_clubs) ;
//  - « Où en est la demande » : envoyée, relances J+3, J+7, J+14, expiration à J+30 ;
//  - les messages prêts à envoyer au bureau (« Prévenir le bureau », « Montrer Arceau au bureau ») ;
//  - la page de vente et « Créer mon équipe » : ce que dit l'état du club, les équipes FFBB déjà sur Arceau.
import type { TeamDraft } from './console';

export type FreeStatus = 'free' | 'validated' | 'refused';
export interface FreeRequest {
  id: string; status: 'open' | 'validated' | 'refused' | 'expired'; created_at: string; sent_at: string | null; reminders: number;
  opened_at: string | null; expires_at: string; closed_at: string | null; next_at: string | null;
}
export interface FreeWaiting {
  team_id: string; name: string; coach: string | null; matches: number; sessions: number; plays: number; play: string | null;
  next_match: { starts_at: string; home: boolean | null; opponent: string | null } | null;
}
export interface FreeClub {
  club_id: string; status: FreeStatus; since: string; plan: string; ffbb_code: string | null; slug: string | null; free_team_id: string | null; free_team: string | null; first_coach: string | null;
  readonly: boolean; purge_on: string | null; trial_until: string | null; staff: boolean; informed_at: string | null; official_email: string | null;
  waiting: FreeWaiting[]; request: FreeRequest | null;
}
/** L'état d'un club sur la page de vente : pas sur Arceau, déjà son équipe gratuite, avec son bureau, a refusé. */
export type ClubState = 'none' | 'free' | 'full' | 'refused';

const DAY = 86400000;
const TZ = 'Europe/Paris';
/** « 1er novembre » (pas « 1 novembre »). */
const fmt = (iso: string, o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('fr-FR', { timeZone: TZ, ...o }).format(new Date(iso)).replace(/(^|\s)1 (?=\p{L})/u, '$11er ');
/** « mercredi 18 novembre » */
export const longDay = (iso: string): string => fmt(iso, { weekday: 'long', day: 'numeric', month: 'long' });
/** « 18 novembre » */
export const dayMonth = (iso: string): string => fmt(iso, { day: 'numeric', month: 'long' });
const plus = (iso: string, days: number) => new Date(Date.parse(iso) + days * DAY).toISOString();
export const REMINDER_DAYS = [3, 7, 14] as const;

/** Une équipe qui attend l'accord du club (dans un club en mode gratuit, toute équipe sauf l'équipe gratuite). */
export const isWaiting = (f: Pick<FreeClub, 'status' | 'free_team_id'> | null | undefined, teamId: string): boolean =>
  !!f && f.status === 'free' && f.free_team_id !== teamId;

export interface WaitStep { state: 'done' | 'wait' | 'todo' | 'off'; title: string; sub: string }
/** « Où en est la demande » : les étapes de la salle d'attente (maquette c2). */
export function requestSteps(r: FreeRequest, officialEmail: string | null): WaitStep[] {
  if (!r.sent_at) {
    return [
      { state: 'off', title: 'Pas encore envoyée', sub: 'Le club n’a pas d’adresse e-mail dans l’annuaire de la FFBB : la demande partira dès qu’elle y sera.' },
      { state: 'todo', title: `Sans réponse le ${longDay(r.expires_at)}`, sub: 'La demande expire ; ton équipe, tes séances et tes plays restent. Tu pourras la refaire.' },
    ];
  }
  const steps: WaitStep[] = [{ state: 'done', title: `Envoyée · ${longDay(r.sent_at)}`, sub: officialEmail ? `À ${officialEmail}, l’adresse que le club publie à la FFBB` : 'À l’adresse que le club publie à la FFBB' }];
  const label = ['1re relance', '2e relance', '3e relance'];
  let shownNext = false;
  REMINDER_DAYS.forEach((d, i) => {
    const at = plus(r.sent_at!, d);
    if (i < r.reminders) steps.push({ state: 'done', title: `${label[i]} · ${longDay(at)}`, sub: r.opened_at ? 'Le lien a été ouvert' : 'Le lien n’a pas encore été ouvert' });
    else if (!shownNext && r.status === 'open') {
      shownNext = true;
      const after = REMINDER_DAYS.slice(i + 1).map(x => longDay(plus(r.sent_at!, x)));
      steps.push({ state: 'wait', title: `${label[i]} · ${longDay(at)}`, sub: after.length ? `Puis ${after.length > 1 ? 'les suivantes' : 'la 3e'}, ${after.join(' et ')}` : 'La dernière' });
    }
  });
  if (r.status === 'expired') steps.push({ state: 'off', title: `Expirée · ${longDay(r.expires_at)}`, sub: 'Ton équipe, tes séances et tes plays restent. Tu peux refaire la demande.' });
  else if (r.status === 'refused') steps.push({ state: 'off', title: 'Le club n’a pas validé', sub: 'Ce que tu as préparé reste.' });
  else steps.push({ state: 'todo', title: `Sans réponse le ${longDay(r.expires_at)}`, sub: 'La demande expire ; ton équipe, tes séances et tes plays restent. Tu pourras la refaire.' });
  return steps;
}

/** Jours depuis l'envoi (« J+4 »), null si rien n'est parti. */
export function dayCount(r: Pick<FreeRequest, 'sent_at'>, now: Date = new Date()): string | null {
  if (!r.sent_at) return null;
  return `J+${Math.max(0, Math.floor((now.getTime() - Date.parse(r.sent_at)) / DAY))}`;
}

/** Le message prêt pour le bureau (salle d'attente : « Prévenir le bureau » ; onglet Club : « Montrer Arceau au bureau »). */
export function bureauMessage(o: { club: string; team: string; waiting?: boolean; link?: string }): string {
  return o.waiting
    ? `Bonjour, j’aimerais utiliser Arceau avec les ${o.team}. La première équipe du club est gratuite ; pour la mienne, le club doit valider. Un e-mail est arrivé à l’adresse du club (celle de l’annuaire FFBB) : « … attend l’accord du club ». Valider, c’est inscrire le club en 2 minutes, avec 30 jours d’essai : les équipes déjà là sont reprises telles quelles. Tu peux regarder ? ${o.link ?? 'arceau.app'}`
    : `Bonjour, j’utilise Arceau avec les ${o.team} (gratuit, une équipe par club). Pour nous les coachs : plays en 3D, séances, convocations, présences, FFBB. Pour le bureau : licences, bénévoles, partenaires, salles, page du club. Le club s’abonne à la saison selon ses licenciés, et tout ce que j’ai fait reste tel quel. On peut l’essayer dans la démo : ${o.link ?? 'arceau.app'}`;
}

/** Ce que dit la page de vente de l'état du club choisi. */
export function stateLine(state: ClubState, club: string): { ok: boolean; text: string } {
  switch (state) {
    case 'none': return { ok: true, text: `${club} n’est pas encore sur Arceau : votre équipe est gratuite toute la saison.` };
    case 'free': return { ok: true, text: `${club} a déjà son équipe gratuite : la vôtre attendra l’accord du club (le bureau valide son inscription).` };
    case 'full': return { ok: false, text: `${club} est déjà sur Arceau : demandez une invitation à son bureau.` };
    default: return { ok: false, text: `${club} n’a pas validé d’équipe sur Arceau.` };
  }
}

/** Les équipes lues à la FFBB, celles déjà sur Arceau marquées (leur engagement est déjà relié). */
export function markTaken(drafts: TeamDraft[], taken: { name: string; coach: string | null; engagements: string[] }[]): (TeamDraft & { taken: string | null })[] {
  return drafts.map(d => {
    const t = taken.find(x => d.links.some(l => x.engagements.includes(l.engagement_id)));
    return { ...d, taken: t ? `Déjà sur Arceau${t.coach ? ` · ${shortName(t.coach)}` : ''}` : null };
  });
}
/** « Inès D. » */
export const shortName = (n: string): string => {
  const [first, ...rest] = n.trim().split(/\s+/);
  return rest.length ? `${first} ${rest[rest.length - 1]!.charAt(0).toUpperCase()}.` : first ?? '';
};

/** Les équipes engagées d'un club, en une ligne : « Départementale 2 · poule B ». */
export const draftLine = (d: TeamDraft): string => {
  const squads = [...new Set(d.links.map(l => l.squad).filter(Boolean))];
  if (squads.length) return squads.join(' · ');
  const l = d.links[0];
  return l ? [l.competition_name, l.poule_name].filter(Boolean).join(' · ') : '';
};

/** « le Basket Club… », « l’Entente… » (comme les e-mails, supabase/functions/_shared/free-mail.ts). */
export const leClub = (name: string): string => {
  const n = name.trim();
  if (/^(le|la|les)\s/i.test(n)) return n;
  return /^[aeiouyhàâäéèêëîïôöûü]/i.test(n) ? `l’${n}` : `le ${n}`;
};
export const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
/** Une catégorie de jeunes (les parents acceptent pour leurs enfants). */
export const youthCategory = (c: string | null | undefined): boolean => /^U(1[0-8]|[0-9])$/i.test(c ?? '');

/** Une adresse e-mail plausible (la page de vente, la validation). */
export const isEmail = (s: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
